import Toast, { ToastPresentationProps } from "@insite/mobius/Toast";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { screen } from "@monsma/XDL/Helpers";
import { mergeWithCss } from "@monsma/XDL/Utilities";
import React, { useContext, useState } from "react";
import styled, { css, withTheme } from "styled-components";

export type NotifyProps = MobiusStyledComponentProps<
    "div",
    {
        /** Text to display in the body of the Toast. Will be ignored if `children` are passed. */
        body?: React.ReactNode;
        /** The type of message to be displayed, which governs color and iconography. */
        messageType?: "success" | "danger" | "info" | "warning";
        /** Description of type of message to be read to screenreader, to replace the iconography and color in relaying
         * the importance or valence of the message. Defaults to `messageType` value if not passed in.
         * Can be used for translations or additional specificity if necessary. */
        messageTypeString?: string;
        onClose?: () => void;
        hideCloseButton?: boolean;
        extendedStyles?: ToastPresentationProps;
    }
>;

export const NotifyBody = styled.div<InjectableCss>`
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    line-height: 22px;
    .title {
        font-weight: 700;
        font-size: 13px;
        line-height: inherit;
        flex-basis: 100%;
        margin-bottom: 5px;
    }
    ${injectCss}
`;

export const notifyStyles: ToastPresentationProps = {
    bodyTypographyProps: {
        size: "13px",
        lineHeight: "22px",
    },
    cssOverrides: {
        toast: css`
            ${getProp("theme.toast.defaultProps.cssOverrides.toast")}
            & {
                margin-top: 0;
                margin-bottom: 20px;
                width: 560px;
                height: auto;

                ${screen("lte", "sm")} {
                    width: 100%;
                }

                ${NotifyBody as any} {
                    font-size: 13px;
                    line-height: 22px;
                }

                ${NotifyBody as any} .title {
                    flex-basis: auto;
                    margin-right: 5px;
                }
            }
        `,
    },
};

const Notify: React.FC<NotifyProps> = ({ children, hideCloseButton = false, extendedStyles, ...otherProps }) => {
    const [showNotify, setShowNotify] = useState(true);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(mergeWithCss(notifyStyles, extendedStyles));
    const removeToast = () => setShowNotify(false);
    let closeButtonProps;
    if (hideCloseButton) {
        closeButtonProps = {
            css: css`
                display: none;
            `,
        };
    }

    const notifyBody = children ? <NotifyBody>{children}</NotifyBody> : null;

    return (
        <ToasterContext.Provider value={{ ...toasterContext, removeToast }}>
            <Toast
                toastId={new Date().getTime()}
                in={showNotify}
                {...otherProps}
                {...styles}
                closeButtonProps={closeButtonProps}
            >
                {notifyBody}
            </Toast>
        </ToasterContext.Provider>
    );
};

export default withTheme(Notify);
